import type { Container } from "./Container.js";
import type { Control } from "./Control.js";
import type { Rect } from "../../common/geometry.js";

/** Which way a directional move is going. */
export type FocusDirection = "up" | "down" | "left" | "right";

/**
 * Tracks the single focused control and implements Tab traversal.
 *
 * Traversal is scoped to the active window, so Tab never walks out of a dialog
 * into the window behind it. Order is tabIndex first, then the order controls
 * were added — the WinForms rule.
 */
export class FocusManager {
  #focused: Control | null = null;
  /** Last focused control per scope, so activating a window restores focus. */
  #lastInScope = new WeakMap<Container, Control>();
  /** Supplies the container Tab traversal is confined to. */
  #scopeProvider: () => Container | null;
  /** Told after every change, so the keyboard bridge can follow. */
  #onChanged: () => void;

  constructor(scopeProvider: () => Container | null, onChanged: () => void = () => {}) {
    this.#scopeProvider = scopeProvider;
    this.#onChanged = onChanged;
  }

  get focused(): Control | null {
    return this.#focused;
  }

  set(control: Control | null): boolean {
    if (control === this.#focused) return true;
    if (control && !control.focusable) return false;

    const previous = this.#focused;
    this.#focused = control;
    if (previous) {
      previous.invalidate();
      previous.emit("blur");
    }
    if (control) {
      const scope = this.#scopeProvider();
      if (scope) this.#lastInScope.set(scope, control);
      control.invalidate();
      control.emit("focus");
    }
    this.#onChanged();
    return true;
  }

  clear(): void {
    this.set(null);
  }

  /** Drop focus if it currently sits on `control` or any of its descendants. */
  clearWithin(control: Control): void {
    if (this.#focused && control.contains(this.#focused)) this.set(null);
  }

  /** Focus whatever had it last in `scope`, else the first tab stop. */
  restoreIn(scope: Container): void {
    const remembered = this.#lastInScope.get(scope);
    if (remembered && remembered.focusable && scope.contains(remembered)) {
      this.set(remembered);
      return;
    }
    const order = tabOrder(scope);
    this.set(order[0] ?? null);
  }

  /**
   * Move focus to the nearest tab stop in `direction`.
   *
   * Tab order is a single line through the whole window, which is the right
   * shape for a form and the wrong one for a d-pad: pressing right on a
   * gamepad means "the thing to my right", not "the next thing that happens
   * to have been added". This picks by position instead.
   *
   * From nothing focused, it behaves like Tab and takes the first stop.
   */
  navigate(direction: FocusDirection): boolean {
    const scope = this.#scopeProvider();
    if (!scope) return false;
    const order = tabOrder(scope);
    if (order.length === 0) {
      this.set(null);
      return false;
    }
    const current = this.#focused;
    if (!current || !order.includes(current)) return this.set(order[0] ?? null);

    const from = current.screenBounds;
    let best: Control | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const candidate of order) {
      if (candidate === current) continue;
      const score = directionScore(from, candidate.screenBounds, direction);
      if (score < bestScore) {
        bestScore = score;
        best = candidate;
      }
    }
    // Nothing that way. Deliberately no wrap-around: a stick held right
    // should stop at the right-hand edge rather than reappear on the left.
    return best ? this.set(best) : false;
  }

  moveNext(): boolean {
    return this.#move(1);
  }

  movePrevious(): boolean {
    return this.#move(-1);
  }

  #move(direction: 1 | -1): boolean {
    const scope = this.#scopeProvider();
    if (!scope) return false;
    const order = tabOrder(scope);
    if (order.length === 0) {
      this.set(null);
      return false;
    }
    const current = this.#focused;
    const index = current ? order.indexOf(current) : -1;
    // Wrap around; from nothing focused, Tab starts at the first stop.
    const next =
      index < 0
        ? direction > 0
          ? 0
          : order.length - 1
        : (index + direction + order.length) % order.length;
    return this.set(order[next] ?? null);
  }
}

/** Focusable descendants of `scope`, in tabIndex-then-insertion order. */
export function tabOrder(scope: Container): Control[] {
  const items: { control: Control; order: number }[] = [];
  let seq = 0;
  for (const c of scope.controls.descendants()) {
    if (c.focusable) items.push({ control: c, order: seq });
    seq++;
  }
  items.sort((a, b) => {
    const d = a.control.tabIndex - b.control.tabIndex;
    return d !== 0 ? d : a.order - b.order;
  });
  return items.map((i) => i.control);
}

/**
 * How good a jump `from` -> `to` is in `direction`. Lower is better, and
 * Infinity means "not that way at all".
 *
 * Distance along the direction is the main cost. Distance across it is
 * charged too, heavily when the two do not overlap on that axis: a control on
 * the same row is what the stick meant, and one further down the screen is a
 * fallback that should lose to anything sensible in line.
 */
function directionScore(from: Rect, to: Rect, direction: FocusDirection): number {
  const fx = from.x + from.width / 2;
  const fy = from.y + from.height / 2;
  const dx = to.x + to.width / 2 - fx;
  const dy = to.y + to.height / 2 - fy;

  const horizontal = direction === "left" || direction === "right";
  const along = direction === "right" ? dx : direction === "left" ? -dx : direction === "down" ? dy : -dy;
  if (along <= 0) return Number.POSITIVE_INFINITY;

  const across = Math.abs(horizontal ? dy : dx);
  const overlaps = horizontal
    ? to.y < from.y + from.height && to.y + to.height > from.y
    : to.x < from.x + from.width && to.x + to.width > from.x;
  return along + across * (overlaps ? 0.25 : 3);
}
